import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff, FaConciergeBell, FaStar, FaEye, FaList, FaTh } from 'react-icons/fa';
import CategoriesList from './CategoriesList';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import { toast } from 'react-toastify';
import './Services.css';
import Pagination from '../Common/Pagination';
import ConfirmModal from '../Common/ConfirmModal';

const ServicesList = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalServices: 0,
    activeServices: 0,
    popularServices: 0,
    totalBookings: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });
  const [activeTab, setActiveTab] = useState('services');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    id: null
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get(ENDPOINTS.SERVICES.CATEGORIES);
        setCategories(response.data || []);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        category: selectedCategory || undefined
      };

      const [servicesRes, statsRes] = await Promise.all([
        api.get(ENDPOINTS.SERVICES.base, { params }),
        api.get(ENDPOINTS.SERVICES.stats)
      ]);

      const servicesData = servicesRes.data || [];
      const statsData = statsRes.data || statsRes;

      setServices(servicesData.data || []);
      setPagination(prev => ({ ...prev, total: servicesData.meta?.total || 0 }));
      setStats({
        totalServices: statsData.totalServices || 0,
        activeServices: statsData.activeServices || 0,
        popularServices: statsData.popularServices || 0,
        totalBookings: statsData.totalBookings || 0
      });
    } catch (error) {
      console.error('Failed to fetch services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (service) => {
    try {
      await api.patch(`${ENDPOINTS.SERVICES.base}/${service.id}`, {
        isActive: !service.isActive
      });
      fetchServices();
    } catch (error) {
      console.error('Failed to toggle status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDelete = (id) => {
    setDeleteModal({ isOpen: true, id });
  };

  const confirmDelete = async () => {
    const { id } = deleteModal;
    try {
      await api.delete(`${ENDPOINTS.SERVICES.base}/${id}`);
      toast.success('Service deleted successfully');
      fetchServices();
      setDeleteModal({ isOpen: false, id: null });
    } catch (error) {
      console.error('Failed to delete service:', error);
      toast.error('Failed to delete service');
    }
  };

  useEffect(() => {
    fetchServices();
  }, [pagination.page, selectedCategory]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Fetching services...</p>
      </div>
    );
  }

  return (
    <div className="services-page">
      <div className="page-header">
        <div>
          <h1>Services Management</h1>
          <p className="page-subtitle">Manage all available services on the platform</p>
        </div>
        <div className="page-actions">
          {activeTab === 'services' && (
            <Link to="/services/create" className="btn btn-primary">
              <FaPlus /> Add Service
            </Link>
          )}
        </div>
      </div>

      <div className="services-tabs-container">
        <div className="services-tabs">
          <button
            className={`tab-btn ${activeTab === 'services' ? 'active' : ''}`}
            onClick={() => setActiveTab('services')}
          >
            Services
          </button>
          <button
            className={`tab-btn ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            Categories
          </button>
        </div>

        {activeTab === 'services' && (
          <div className="flex gap-3 items-center">
            <select
              className="form-select"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #ddd', marginRight: '10px' }}
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>

            <div className="view-toggle">
              <button
                className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="List View"
              >
                <FaList />
              </button>
              <button
                className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid View"
              >
                <FaTh />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      {/* Stats - Only show for Services tab */}
      {activeTab === 'services' && (
        <div className="services-stats">
          <div className="stat-mini-card">
            <div className="stat-mini-icon">
              <FaConciergeBell />
            </div>
            <div className="stat-mini-content">
              <h3>{stats.totalServices}</h3>
              <p>Total Services</p>
            </div>
          </div>
          <div className="stat-mini-card">
            <div className="stat-mini-icon success">
              <FaToggleOn />
            </div>
            <div className="stat-mini-content">
              <h3>{stats.activeServices}</h3>
              <p>Active</p>
            </div>
          </div>
          {/* <div className="stat-mini-card">
            <div className="stat-mini-icon popular">
              <FaStar />
            </div>
            <div className="stat-mini-content">
              <h3>{stats.popularServices}</h3>
              <p>Popular</p>
            </div>
          </div> */}
          <div className="stat-mini-card">
            <div className="stat-mini-icon">
              <FaConciergeBell />
            </div>
            <div className="stat-mini-content">
              <h3>{stats.totalBookings}</h3>
              <p>Total Bookings</p>
            </div>
          </div>
        </div>
      )}

      {/* Services Grid */}
      {/* Content Area */}
      {activeTab === 'categories' ? (
        <CategoriesList />
      ) : (
        <>
          {/* Services Content */}
          {viewMode === 'grid' ? (
            <div className="services-grid">
              {services.map((service) => (
                <div key={service.id} className={`service-card ${!service.isActive ? 'inactive' : ''}`}>
                  <div className="service-header">
                    <div className="service-category">{service.category?.name || (typeof service.category === 'string' ? service.category : 'Uncategorized')}</div>
                    {service.isPopular && (
                      <div className="popular-badge">
                        <FaStar /> Popular
                      </div>
                    )}
                  </div>

                  <div className="service-body">
                    <h3 className="service-title">{service.name}</h3>
                    <p className="service-description">{service.description}</p>

                    <div className="service-meta">
                      <div className="meta-item">
                        <label>Base Price</label>
                        <span className="price-value">
                          ${service.basePrice}
                          {service.pricingType === 'monthly' || 
                           service.category?.name?.toLowerCase().includes('confinement') || 
                           service.category?.slug?.toLowerCase().includes('confinement') ? '/mo' : '/hr'}
                        </span>
                      </div>
                      {/* {!(service.pricingType === 'monthly' || 
                         service.category?.name?.toLowerCase().includes('confinement') || 
                         service.category?.slug?.toLowerCase().includes('confinement')) && (
                        <div className="meta-item">
                          <label>Duration</label>
                          <span className="duration-value">{service.durationMinutes} min</span>
                        </div>
                      )} */}
                    </div>

                    <div className="service-stats">
                      <div className="stat-item">
                        <span className="stat-value">{service.totalProviders || 0}</span>
                        <span className="stat-label">Providers</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-value">{service.totalBookings || 0}</span>
                        <span className="stat-label">Bookings</span>
                      </div>
                    </div>
                  </div>

                  <div className="service-footer">
                    <div className="service-status" onClick={() => handleToggleStatus(service)} style={{ cursor: 'pointer' }}>
                      {service.isActive ? (
                        <span className="badge badge-success">
                          <FaToggleOn /> Active
                        </span>
                      ) : (
                        <span className="badge badge-danger">
                          <FaToggleOff /> Inactive
                        </span>
                      )}
                    </div>
                    <div className="action-buttons">
                      <button
                        className="btn-icon view"
                        title="View Details"
                        onClick={() => navigate(`/services/${service.id}`)}
                      >
                        <FaEye />
                      </button>
                      <button
                        className="btn-icon edit"
                        title="Edit"
                        onClick={() => navigate(`/services/${service.id}/edit`)}
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="btn-icon delete"
                        title="Delete"
                        onClick={() => handleDelete(service.id)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* List View */
            <div className="services-list-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Price</th>
                    {/* <th>Duration</th> */}
                    <th className="text-center">Status</th>
                    <th className="text-center">Providers</th>
                    <th className="text-center">Bookings</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((service) => (
                    <tr key={service.id}>
                      <td>
                        <div className="service-name-cell">
                          <span className="list-service-name">{service.name}</span>
                          <span className="list-service-category">{service.category?.name || (typeof service.category === 'string' ? service.category : 'Uncategorized')}</span>
                          {service.isPopular && <span className="popular-tag"><FaStar size={10} color="#fed330" /> Popular</span>}
                        </div>
                      </td>
                      <td>
                        ${service.basePrice}
                        {service.pricingType === 'monthly' || 
                         service.category?.name?.toLowerCase().includes('confinement') || 
                         service.category?.slug?.toLowerCase().includes('confinement') ? '/mo' : '/hr'}
                      </td>
                      {/* <td>
                        {!(service.pricingType === 'monthly' || 
                           service.category?.name?.toLowerCase().includes('confinement') || 
                           service.category?.slug?.toLowerCase().includes('confinement')) ? `${service.durationMinutes} min` : '-'}
                      </td> */}
                      <td className="text-center">
                        <div onClick={() => handleToggleStatus(service)} style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                          {service.isActive ? (
                            <>
                              <FaToggleOn color="#2ecc71" size={20} /> <span style={{ fontSize: '12px', color: '#2ecc71' }}>Active</span>
                            </>
                          ) : (
                            <>
                              <FaToggleOff color="#e74c3c" size={20} /> <span style={{ fontSize: '12px', color: '#e74c3c' }}>Inactive</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="text-center">{service.totalProviders || 0}</td>
                      <td className="text-center">{service.totalBookings || 0}</td>
                      <td className="text-center">
                        <div className="action-buttons">
                          <button
                            className="btn-icon view"
                            title="View Details"
                            onClick={() => navigate(`/services/${service.id}`)}
                          >
                            <FaEye />
                          </button>
                          <button
                            className="btn-icon edit"
                            title="Edit"
                            onClick={() => navigate(`/services/${service.id}/edit`)}
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="btn-icon delete"
                            title="Delete"
                            onClick={() => handleDelete(service.id)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          <Pagination 
            currentPage={pagination.page}
            totalItems={pagination.total}
            itemsPerPage={pagination.limit}
            onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          />
        </>
      )}
      <ConfirmModal 
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, id: null })}
        onConfirm={confirmDelete}
        title="Delete Service?"
        message="Are you sure you want to delete this service? This will permanently remove the service and may affect existing bookings. This action cannot be undone."
        confirmText="Permanently Delete"
      />
    </div>
  );
};

export default ServicesList;